<template>
  <div class="settings-section">
    <div class="row justify-between items-center q-gutter-y-sm"> 
      <h4 class="text-h4 q-my-none">{{ $t('bookings.viewPhotographers') }}</h4>
      <q-btn
        :label="$t('bookings.goToBookings')"
        icon="event_available"
        color="secondary"
        unelevated
        rounded
        @click="goToBookings"
      />
    </div>

    <div v-if="photographersListLoading || isLoadingUser">
      <q-skeleton height="80px" class="q-mb-md" type="rect" />
      <q-skeleton height="80px" class="q-mb-md" type="rect" />
      <q-skeleton height="80px" class="q-mb-md" type="rect" />
    </div>

    <div v-if="!isLoadingUser && photographersListFailed" class="q-my-lg">
      <q-banner rounded class="bg-red-2 text-red-9">
        {{ $t('bookings.photographersLoadError') }}
      </q-banner>
    </div>

    <div v-if="!isLoadingUser && !photographersListLoading && !photographersListFailed && getPhotographersList?.photographers?.length === 0" class="q-my-lg">
      <q-banner rounded class="bg-blue-1 text-blue-9">
        {{ $t('bookings.noPhotographersAvailable') }}
      </q-banner>
    </div>

    <div v-if="!photographersListLoading && !photographersListFailed && !isLoadingUser && getPhotographersList?.photographers?.length > 0">
      <q-card
        v-for="p in getPhotographersList?.photographers || []"
        :key="p.userId"
        class="q-pa-md q-mb-md"
        flat
        bordered
      >
        <div class="row items-center justify-between q-gutter-x-md">
          <div class="col">
            <div class="text-subtitle1">
              {{ displayNameOrUsernameFallback(p) }}
            </div>
            <div class="text-caption text-grey-7">
              <strong>{{ $t("bookings.available") }}</strong>
            </div>
            <div class="text-caption text-grey-7">
              {{ $t('bookings.region') }}:
              {{ p.photographerInformation?.photographerRegion }}
            </div>
          </div>
          <div class="col-auto column q-gutter-sm">
            <q-btn
              v-if="p.photographerInformation?.landingUrl"
              color="secondary"
              outline
              :href="p.photographerInformation.landingUrl"
              target="_blank"
              :label="$t('bookings.viewProfile')"
              class="bookings-form-button"
            />
            <q-btn
              v-if="canBook"
              unelevated
              color="primary"
              @click="openBookingModal(p)"
              :label="$t('bookings.book')"
              class="bookings-form-button"
            />
          </div>
        </div>
      </q-card>
    </div>

    <PhotographerBookingForm
      v-model="isBookingModalOpen"
      :photographer="selectedPhotographer"
      @booking-submitted="handleBookingSubmit"
      @close="closeBookingModal"
    />
  </div>
</template>

<script>
import { Notify } from 'quasar';
import { mapActions, mapState, mapGetters } from 'vuex';
import PhotographerBookingForm from './PhotographerBookingForm.vue';

export default {
  name: 'PhotographersListing',
  components: {
    PhotographerBookingForm,
  },
  props: {
    canBook: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBookingModalOpen: false,
      selectedPhotographer: null,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      isLoadingUser: (state) => state.auth.isLoadingUser,
      photographersListLoading: (state) =>
        state.bookings.photographersListLoading,
      photographersListFailed: (state) =>
        state.bookings.photographersListFailed,
    }),
    ...mapGetters('bookings', ['getPhotographersList']),
    canBookPhotographer() {
      return (
        this.user?.role === 'organizer'
      );
    },
  },
  methods: {
    ...mapActions('bookings', ['fetchPhotographersList', 'createBooking']),
    ...mapActions('auth', ['getUserInfo']),
    goToBookings() {
      this.$router.push({ name: 'user-bookings' });
    },
    displayNameOrUsernameFallback(p) {
      const first = p.firstName?.trim();
      const last = p.lastName?.trim();

      if (first || last) {
        return `${first || ''} ${last || ''}`.trim();
      }

      return p.userName;
    },
    closeBookingModal() {
      this.isBookingModalOpen = false;
      this.selectedPhotographer = null;
    },
    openBookingModal(photographer) {
      this.selectedPhotographer = photographer;
      this.isBookingModalOpen = true;
    },

    handleBookingSuccess(formData) {
      this.submitBooking(this.selectedPhotographer.userId, formData);
      this.$router.push({ name: 'user-bookings' });
    },
    async handleBookingSubmit(formData) {
      if (!this.selectedPhotographer) {
        return;
      }

      const bookingPayload = {
        photographer_id: this.selectedPhotographer.userId,
        ...formData,
      };

      try {
        await this.createBooking(bookingPayload);
        Notify.create({
          message: this.$t('bookings.store.success'),
          type: 'positive',
        });
        this.closeBookingModal();
        this.$router.push({ name: 'user-bookings' });
      } catch (error) {
        console.error(error);
        Notify.create({
          message: this.$t(error.response?.data?.message) || this.$t('bookings.store.error'),
          type: 'negative',
        });
      }
    },
    showNotification(message, type) {
      Notify.create({
        message: this.$t(message),
        type,
        position: 'bottom',
        timeout: 3000,
      });
    },
  },
  async mounted() {
    if (this.user?.id) {
      await this.getUserInfo();
    }
    await this.fetchPhotographersList();
  },
};
</script>

<style lang="scss" scoped>
.settings {
  &-section {
    @extend .settings-section;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    margin: 0 auto;
    box-shadow: none;
    gap: 2.5rem;
  }
}

@media screen and (min-width: $breakpoint-md-min) {
  .settings {
    &-section {
      padding: 1.5rem 2.5rem;
      gap: 3rem;
    }
  }
}
</style>
